import * as DOMPurify from "dompurify";
import type { ConfigOptions, Hooks, RemovedItem } from "./domPurify-config";
import * as domPurifyConfig from "./domPurify-config";

/**
 * Validates the removed items during the sanitization process.
 * This function checks if the removed items/attributes are in the  block list. If they are not,
 * there is a content loss.
 * Possible scenarios:
 * [original HTML] if:
 * 1. The removedTags/missingAttributes set doesn't include any tags/attrs from the htmlBlockList.
 * [sanitizedHTML] if:
 * 1. The removedTags/missingAttributes set include tags/attrs from the htmlBlockList.
 */
const checkRemovedItems = (removedItems: RemovedItem[]) => {
  let contentLoss = false;
  let hasForbiddenTags = false;
  const missingAttributes = new Set<string>();
  const removedTags = new Set<string>();

  if (removedItems.length > 0) {
    removedItems.forEach((item: RemovedItem) => {
      if (item.attribute) {
        hasForbiddenTags =
          domPurifyConfig.htmlBlockListAttributes.includes(
            item.attribute.localName
          ) || hasForbiddenTags;
        missingAttributes.add(item.attribute.localName);
      } else if (item.element) {
        hasForbiddenTags =
          domPurifyConfig.htmlBlockList.includes(item.element.localName) ||
          hasForbiddenTags;
        removedTags.add(item.element.localName);
      }
    });
    if (missingAttributes.size > 0) {
      if (!hasForbiddenTags) {
        contentLoss = true;
      }
    } else if (removedTags.size > 0) {
      if (!hasForbiddenTags) {
        contentLoss = true;
      }
    }
  }
  return contentLoss;
};
/**
 * Set the configuration for the sanitize method.
 * [IMPORTANT]: This configuration needs to be explicitly defined to ensure that minification works correctly in different contexts.
 * Values based on official documentation: https://github.com/cure53/DOMPurify/blob/main/src/purify.ts
 * @param {ConfigOptions} config - custom configuration for sanitize method.
 * @returns default config with the custom config
 */
const setCustomConfig = (config: ConfigOptions) => {
  const customConfig: ConfigOptions = {
    ...domPurifyConfig.defaultConfig,
    ALLOW_ARIA_ATTR: config["ALLOW_ARIA_ATTR"] !== false,
    ALLOW_DATA_ATTR: config["ALLOW_DATA_ATTR"] !== false,
    FORCE_BODY: config["FORCE_BODY"] || false,
    IN_PLACE: config["IN_PLACE"] || false,
    SAFE_FOR_TEMPLATES: config["SAFE_FOR_TEMPLATES"] || false,
    SANITIZE_DOM: config["SANITIZE_DOM"] !== false,
    SANITIZE_NAMED_PROPS: config["SANITIZE_NAMED_PROPS"] || false,
    USE_PROFILES: config["USE_PROFILES"] ? config["USE_PROFILES"] : false,
    WHOLE_DOCUMENT: config["WHOLE_DOCUMENT"] || false,
    PARSER_MEDIA_TYPE: config["PARSER_MEDIA_TYPE"] ?? "text/html",
  };

  return customConfig;
};

// Universal DOMPurify instance for all environments (e.g. browser/playwright and node/jest)
const domPurifyInstance = (DOMPurify as any).default || DOMPurify;

/**
 * Sanitizes the HTML content using DOMPurify and validates the removed items against a block list.
 * @param {string} html - The HTML content to be sanitized.
 * @param {ConfigOptions} [config] - Optional configuration for the sanitize method.
 * @param {Hooks} [hooks] - Optional hooks to be applied during sanitization.
 * @returns {string} The sanitized HTML content.
 */
export const sanitizedHTMLAndValidate = (
  html: string,
  config?: ConfigOptions,
  hooks?: Hooks
) => {
  let hasModifiedHTML = false;

  const sanitizationConfig = config
    ? setCustomConfig(config)
    : domPurifyConfig.defaultConfig;

  // Apply any custom hooks that were provided
  if (hooks) {
    Object.entries(hooks).forEach(([hookName, hookFn]) => {
      domPurifyInstance.addHook(hookName as any, hookFn);
    });
  }

  const sanitizedHTML = domPurifyInstance.sanitize(html, sanitizationConfig);

  // once HTML is sanitized, removing the hooks to avoid side effects
  if (hooks) {
    Object.entries(hooks).forEach(([hookName]) => {
      domPurifyInstance.removeHooks(hookName as any);
    });
  }

  if (config?.USE_PROFILES) {
    return sanitizedHTML;
  }

  hasModifiedHTML = checkRemovedItems(domPurifyInstance.removed);

  return hasModifiedHTML ? html : sanitizedHTML;
};

export const domPurifyIsValidAttribute = domPurifyInstance.isValidAttribute;
